import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PrebuiltSettings, selectedMembership, useAxios } from '@intly/core';
import DialogTitle from '../../Dialog/DialogTitle';
import Banner from '../../../ui/Banner/Banner';
import {
  COST_CENTER_REVIEW,
  CostCenterReviewEmployeeRows,
  ReviewCell,
  ReviewEmployeeRef,
} from './costCenterReview.types';
import { describeRow } from './reviewDerived';
import { tableSx } from './reviewTableStyles';

interface ReviewEmployeesDialogProps {
  cell: ReviewCell | null;
  onClose: () => void;
  // Opens the "Expandera" postings of the cell, limited to the clicked employee
  onSelectEmployee: (employee: ReviewEmployeeRef) => void;
  companyId: string;
  companyName: string;
  costCenterColumn: string | null;
  costCenter: string | null;
  costCenterLabel: string;
  settings: PrebuiltSettings;
}

const amountFormat = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// "Expandera per anställd": the postings behind one review cell summed per employee,
// adding up to the cell. Clicking an employee lists their postings.
const ReviewEmployeesDialog: FC<ReviewEmployeesDialogProps> = ({
  cell,
  onClose,
  onSelectEmployee,
  companyId,
  companyName,
  costCenterColumn,
  costCenter,
  costCenterLabel,
  settings,
}) => {
  const { t } = useTranslation();
  const { backendDataRequest } = useAxios();
  const groupId = useReactiveVar(selectedMembership);

  const account = cell?.row.account;
  // An account row covers every category; a detail row one (possibly none, '').
  const category =
    cell?.row.kind === 'detail' ? cell.row.description ?? '' : undefined;

  const query = useQuery<CostCenterReviewEmployeeRows>({
    queryKey: [
      'prebuilt',
      COST_CENTER_REVIEW,
      'employees',
      groupId,
      companyId,
      costCenter,
      cell?.period,
      account,
      category,
      settings,
    ],
    enabled: Boolean(cell && groupId),
    staleTime: Infinity,
    queryFn: async () => {
      const response = await backendDataRequest('data', {
        method: 'POST',
        data: {
          function: 'prebuilt_dashboard_employee_rows',
          groupId,
          dashboard: COST_CENTER_REVIEW,
          companyId,
          period: cell?.period,
          costCenterColumn,
          costCenter,
          cell: { account, category },
          settings,
        },
      });
      if (!response?.data || response.data.error) {
        throw new Error(response?.data?.error ?? 'No result');
      }
      return response.data as CostCenterReviewEmployeeRows;
    },
  });

  const rowLabel = cell
    ? cell.row.kind === 'account'
      ? describeRow(cell.row)
      : `${cell.row.account} · ${describeRow(cell.row)}`
    : '';
  const data = query.data;

  return (
    <Dialog fullWidth maxWidth="md" open={Boolean(cell)} onClose={onClose}>
      <DialogTitle onClose={onClose}>
        {cell
          ? `${t('REVIEW_EXPAND_PER_EMPLOYEE')} ${cell.row.account} ${
              cell.period
            }`
          : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${t('COMPANY')}: ${companyName} · ${t(
            'COST_CENTER'
          )}: ${costCenterLabel} · ${rowLabel}`}
          {data &&
            ` · ${t('REVIEW_EMPLOYEES_COUNT', { count: data.rows.length })}`}
        </Typography>

        {query.isError && (
          <Banner
            severity="error"
            message={`${t('REVIEW_TRANSACTIONS_LOAD_ERROR')}: ${
              (query.error as Error).message
            }`}
          />
        )}
        {data?.truncated && (
          <Banner
            severity="warning"
            message={t('REVIEW_TRANSACTIONS_TRUNCATED') as string}
          />
        )}
        {query.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {data && !data.rows.length && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            {t('REVIEW_TRANSACTIONS_EMPTY')}
          </Typography>
        )}
        {data && data.rows.length > 0 && (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 'calc(100vh - 260px)' }}
          >
            <Table stickyHeader size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('FTE_DRILLDOWN_EMPLOYEE')}</TableCell>
                  <TableCell align="right">
                    {t('FTE_DRILLDOWN_EMPLOYEE_NUMBER')}
                  </TableCell>
                  <TableCell align="right">{t('AMOUNT')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row) => (
                  <TableRow
                    key={row.employeeNumber}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      onSelectEmployee({
                        number: row.employeeNumber,
                        name: row.name,
                      })
                    }
                  >
                    <TableCell>
                      {row.name ||
                        (row.employeeNumber ? '' : t('REVIEW_NO_EMPLOYEE'))}
                    </TableCell>
                    <TableCell align="right">{row.employeeNumber}</TableCell>
                    <TableCell align="right">
                      {amountFormat.format(row.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: 'background.paper',
                  '& .MuiTableCell-root': {
                    fontWeight: 'bold',
                    color: 'text.primary',
                  },
                }}
              >
                <TableRow>
                  <TableCell>{t('REVIEW_TOTAL')}</TableCell>
                  <TableCell />
                  <TableCell align="right">
                    {amountFormat.format(data.total)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewEmployeesDialog;
